import React, { useRef, useState } from 'react';
import Button from '@/components/Button';
import { CalendarDays } from '@/components/Icons';
import YearPicker from '@/components/YearPicker';
import cn from '@/utils/cn';
import { useClickOutside } from '@/hooks/useClickOutside';

interface YearPickerInputProps {
  selectedYear: number | null;
  onSelectYear: (year: number | null) => void;
  minDate: Date | null;
  maxDate: Date | null;
  defaultLabel?: string;
  className?: string;
}

const pillButtonClass = cn([
  'border',
  'border-emerge-navy-4',
  'text-emerge-navy',
  'text-xs',
  'font-medium',
  'rounded-full',
  'py-2',
  'pl-3',
  'pr-4',
  'flex',
  'items-center',
  'gap-x-2',
  'hover:bg-emerge-base-2',
  'active:bg-emerge-base-2',
  'focus:outline-none',
  'focus:ring',
  'focus:ring-aux-gray',
  'focus:bg-emerge-base-2'
]);

const YearPickerInput: React.FC<YearPickerInputProps> = ({
  selectedYear,
  onSelectYear,
  minDate,
  maxDate,
  defaultLabel = 'Last 12 months',
  className
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useClickOutside(containerRef, () => setIsOpen(false));

  const handleSelectYear = (year: number) => {
    onSelectYear(year);
    setIsOpen(false);
  };

  const handleToday = () => {
    onSelectYear(new Date().getFullYear());
    setIsOpen(false);
  };

  const handleClear = () => {
    onSelectYear(null);
    setIsOpen(false);
  };

  return (
    <div className={cn('relative inline-block', className)} ref={containerRef}>
      <Button
        onClick={() => setIsOpen((prev) => !prev)}
        className={pillButtonClass}
        variant="secondary">
        <CalendarDays />
        <span className="flex-1 text-left">{selectedYear ?? defaultLabel}</span>
      </Button>

      {isOpen && (
        <div className="absolute z-10 mt-1">
          <YearPicker
            selectedYear={selectedYear}
            onSelectYear={handleSelectYear}
            onToday={handleToday}
            onClear={handleClear}
            minDate={minDate}
            maxDate={maxDate}
          />
        </div>
      )}
    </div>
  );
};

export default YearPickerInput;
